import { createContext, useContext, type ReactNode } from 'react';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../__generated__/schema-lite';

/** Per-account role. Hand-written because the web bundle generates no model types
 *  (`--generate-models false`) — the lite schema carries shapes for queries, not
 *  enums to import. Keep in sync with `AccountRole` in schema.zmodel. */
export type AccountRole = 'OWNER' | 'ADMIN' | 'RESPONDER' | 'VIEWER';

/** One row of `myAccounts()` — a line the signed-in person can act in. */
export type MyAccount = {
  accountId: string;
  label: string | null;
  e164: string | null;
  myRole: AccountRole;
  unread: number;
  /** No ACTIVE number yet — the line cannot receive a call, so the app sends the
   *  owner into the wizard instead of a working screen. Always false for a delegate,
   *  who does not onboard someone else's line. */
  needsOnboarding: boolean;
  /** Has a number but the wizard was left incomplete or an optional step skipped —
   *  a "finish setup" banner, not a block. */
  setupUnfinished: boolean;
};

/** Every line the caller can act in. One request for the whole set, so the picker,
 *  the header and the route guard all read the same answer instead of each asking. */
export function useMyAccounts() {
  const crud = useClientQueries(schema);
  const q = crud.$procs.myAccounts.useQuery();
  const accounts = ((q.data as { accounts?: MyAccount[] } | undefined)?.accounts ??
    []) as MyAccount[];
  return { accounts, isLoading: q.isLoading, error: q.error };
}

const AccountCtx = createContext<MyAccount | null>(null);

export function AccountProvider({ value, children }: { value: MyAccount; children: ReactNode }) {
  return <AccountCtx.Provider value={value}>{children}</AccountCtx.Provider>;
}

/** The line currently being viewed, as named by the URL.
 *
 *  This is the only correct way for a screen to answer "which account am I in" — it
 *  resolves from `/a/:accountId`, so two tabs give two different answers and a refresh
 *  gives the same one. See `last-account.ts` for the thing not to reach for instead. */
export function useCurrentAccount(): MyAccount {
  const ctx = useContext(AccountCtx);
  if (!ctx) throw new Error('useCurrentAccount must be used inside <AccountScope>');
  return ctx;
}

/** Same, for a component that may legitimately render outside a line — a placeholder
 *  screen, an error boundary. Throwing there would turn "no account in context" into a
 *  blank app, which is a worse answer than showing the screen without the line on it. */
export function useCurrentAccountOrNull(): MyAccount | null {
  return useContext(AccountCtx);
}

/** Convenience for building links inside the current line: `${base}/settings`. */
export function useAccountBase(): string {
  return `/a/${useCurrentAccount().accountId}`;
}
